import React, { useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row, Button, Form, Spinner } from 'react-bootstrap';
import AssignedProcessorsMultiSelect from '../../../AssignedProcessors';
import TaskOrderPicker from './TaskOrderPicker';
import RichTextEditor from '../../../common/RichTextEditor';
import moment from 'moment';
import { getUserFromLocalstrorage, addBusinessDays, getBusinessDaysDiff, normalizeRichTextValue } from '../../../../helpers/global';
import { toRichTextEditorValue } from '../../../../helpers/safeHtml';

const baseFormFieldError = [
  { key: 'title', isNotValid: false, type: 'string' },
  { key: 'assignedProcessors', isNotValid: false, type: 'array' },
  { key: 'dueDate', isNotValid: false, type: 'string' },
  { key: 'dueInBusinessDays', isNotValid: false, type: 'number' }
];

const toProcessorIds = processors =>
  (processors || []).map(p => (typeof p === 'object' && p !== null ? p._id : p)).filter(Boolean);

const emptyFormData = user => ({
  assignedProcessors: user?._id ? [user._id] : [],
  title: '',
  description: '',
  dueDate: '',
  dueInBusinessDays: '',
  order: null
});

const checkIsValid = (formData, fieldError = []) => {
  let error = false;
  return {
    result: fieldError.map(field => {
      const key = field.key;
      const value = formData[key];
      const type = field.type;
      if (type === 'array') {
        if (value.length <= 0) {
          error = true;
          return { key, isNotValid: true, type };
        }
        return { key, isNotValid: false, type };
      }
      if (type === 'string') {
        if (value === '' || value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
          error = true;
          return { key, isNotValid: true, type };
        }
        return { key, isNotValid: false, type };
      }
      if (type === 'number') {
        const num = Number(value);
        if (
          value === '' ||
          value === null ||
          value === undefined ||
          !Number.isFinite(num) ||
          !Number.isInteger(num) ||
          num < 0
        ) {
          error = true;
          return { key, isNotValid: true, type };
        }
        return { key, isNotValid: false, type };
      }
      if (type === 'object') {
        if (value === null || value === undefined) {
          error = true;
          return { key, isNotValid: true, type };
        }
        return { key, isNotValid: false, type };
      }
      return { key, isNotValid: false, type };
    }),
    error
  };
};

const updateOnChangeErrorField = (fieldError = [], key) => {
  return fieldError.map(field => {
    if (field.key === key) {
      return { key, isNotValid: false, type: field.type };
    }
    return field;
  });
};

const AddEditTaskModal = ({ show, onHide, onSubmit, showProgress, edit, requireServiceSelection = false }) => {
  const user = useMemo(() => getUserFromLocalstrorage(), []);
  const isEditing = Boolean(edit?._id);
  const showServiceFields = requireServiceSelection && !isEditing;

  const initialFormFieldError = useMemo(() => [...baseFormFieldError], []);

  const [formData, setFormData] = useState(emptyFormData(user));
  const [excludeFridays, setExcludeFridays] = useState(false);
  const [formError, setFormError] = useState(initialFormFieldError);
  const [showTaskOrderModal, setShowTaskOrderModal] = useState(false);

  useEffect(() => {
    setFormError(initialFormFieldError);
  }, [initialFormFieldError]);

  const handleSubmit = () => {
    const { result, error } = checkIsValid(formData, formError);
    if (error) {
      setFormError(result);
    } else {
      const { dueInBusinessDays, ...restFormData } = formData;
      const payload = {
        ...restFormData,
        title: (restFormData.title || '').trim(),
        description: normalizeRichTextValue(restFormData.description) || '',
        assignedProcessors: restFormData.assignedProcessors,
        status: isEditing ? edit?.status ?? 'Incomplete' : 'Incomplete',
        excludeFridays
      };
      if (showServiceFields) {
        payload.order = formData.order;
      }
      onSubmit(payload);
    }
  };

  useEffect(() => {
    if (!show) {
      setFormData(emptyFormData(user));
      setExcludeFridays(false);
      setFormError(initialFormFieldError);
    }
  }, [show, user, initialFormFieldError]);

  useEffect(() => {
    if (isEditing && !edit?.loading) {
      const today = moment().startOf('day');
      const dueDateMoment = moment(edit?.dueDate);
      const fridaysExcluded = Boolean(edit.excludeFridays);
      const hasTitle = Boolean(edit.title && String(edit.title).trim());
      setExcludeFridays(fridaysExcluded);
      setFormData({
        ...emptyFormData(user),
        assignedProcessors: toProcessorIds(edit.assignedProcessors),
        // Legacy tasks used description as the headline — map into Title when title is missing.
        title: hasTitle ? edit.title : String(edit.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
        description: hasTitle ? toRichTextEditorValue(edit.description || '') : '',
        dueDate: dueDateMoment.format('YYYY-MM-DD'),
        dueInBusinessDays: getBusinessDaysDiff(today, dueDateMoment, fridaysExcluded)
      });
    }
  }, [edit, isEditing, user]);

  const isFieldNotValid = key => {
    const field = formError.find(f => f.key === key);
    return field?.isNotValid;
  };

  const clearDueFieldErrors = () => {
    setFormError(prev => {
      const clearedDueDate = updateOnChangeErrorField(prev, 'dueDate');
      return updateOnChangeErrorField(clearedDueDate, 'dueInBusinessDays');
    });
  };

  const onDueFieldsChange = e => {
    const { id, value } = e?.target || {};
    const today = moment().startOf('day');

    if (!value && value !== 0 && value !== '0') {
      setFormData(prev => ({ ...prev, dueDate: '', dueInBusinessDays: '' }));
      clearDueFieldErrors();
      return;
    }

    if (id === 'dueInBusinessDays') {
      const sanitizedDueInBusinessDays = String(value).replace(/\D/g, '');

      if (sanitizedDueInBusinessDays === '') {
        setFormData(prev => ({ ...prev, dueDate: '', dueInBusinessDays: '' }));
        clearDueFieldErrors();
        return;
      }

      const dueDateMoment = addBusinessDays(today, sanitizedDueInBusinessDays, excludeFridays);

      setFormData(prev => ({
        ...prev,
        dueInBusinessDays: sanitizedDueInBusinessDays,
        dueDate: dueDateMoment ? dueDateMoment.format('YYYY-MM-DD') : prev.dueDate
      }));
    } else if (id === 'dueDate') {
      const dueDateMoment = moment(value, 'YYYY-MM-DD');
      const businessDays = getBusinessDaysDiff(today, dueDateMoment, excludeFridays);

      setFormData(prev => ({
        ...prev,
        dueDate: value,
        dueInBusinessDays: businessDays
      }));
    }

    clearDueFieldErrors();
  };

  const onExcludeFridaysChange = e => {
    const checked = e.target.checked;
    setExcludeFridays(checked);
    const today = moment().startOf('day');
    setFormData(prev => {
      const n = Number(prev.dueInBusinessDays);
      if (prev.dueInBusinessDays !== '' && Number.isInteger(n) && n >= 0) {
        const dueDateMoment = addBusinessDays(today, n, checked);
        return {
          ...prev,
          dueDate: dueDateMoment ? dueDateMoment.format('YYYY-MM-DD') : prev.dueDate
        };
      }
      if (prev.dueDate) {
        const d = moment(prev.dueDate, 'YYYY-MM-DD');
        if (!d.isValid()) return prev;
        return { ...prev, dueInBusinessDays: getBusinessDaysDiff(today, d, checked) };
      }
      return prev;
    });
    clearDueFieldErrors();
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
      setFormError(updateOnChangeErrorField(formError, field));
    }
  };

  const onDescriptionChange = html => {
    setFormData(prev => ({ ...prev, description: html }));
  };

  const handleAssignedProcessorsChange = processors => {
    setFormData(prevData => ({
      ...prevData,
      assignedProcessors: processors.map(p => p._id)
    }));
    setFormError(updateOnChangeErrorField(formError, 'assignedProcessors'));
  };

  return (
    <>
      {showServiceFields && (
        <TaskOrderPicker
          show={showTaskOrderModal}
          onHide={() => setShowTaskOrderModal(false)}
          selectedOrder={formData.order}
          submit={order => {
            setFormData(prev => ({ ...prev, order }));
            setFormError(prev => updateOnChangeErrorField(prev, 'order'));
            setShowTaskOrderModal(false);
          }}
        />
      )}

      <Modal show={show} onHide={onHide} centered backdrop="static">
        <Modal.Header closeButton={!showProgress}>
          <Modal.Title>{isEditing ? 'Update Task' : 'New Task'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {showServiceFields && (
              <Col md={12}>
                <Form.Group className="midFont flex-column mb-3">
                  <Form.Label>Select Order</Form.Label>
                  <button
                    type="button"
                    onClick={() => setShowTaskOrderModal(true)}
                    className="hover-muted bg-light shadow-none text-dark text-left form-control form-control-sm smallFont"
                    style={{ height: '32px', borderColor: '#ced4da' }}
                  >
                    <span className="smallFont">
                      {formData.order
                        ? `${formData.order.service?.propertyAddress || 'N/A'} - ${formData.order.orderType}`
                        : 'Tap to Select Order'}
                    </span>
                  </button>
                </Form.Group>
              </Col>
            )}

            <Col md={12}>
              <Form.Group className="midFont">
                <Form.Label>
                  Title<span className="text-danger">*</span>
                </Form.Label>
                <input
                  id="title"
                  onChange={e => onFormDataChange('title', e)}
                  type="text"
                  value={formData.title}
                  className={`form-control form-control-sm mt-0 ${isFieldNotValid('title') && 'input-invalid'}`}
                />
                {isFieldNotValid('title') && <p className="xsmallFont text-rush">This is a required Field</p>}
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="midFont">
                <Form.Label>
                  Assigned Processor<span className="text-danger">*</span>
                </Form.Label>
                <AssignedProcessorsMultiSelect
                  id="taskAssignedProcessors"
                  error={isFieldNotValid('assignedProcessors')}
                  onChange={handleAssignedProcessorsChange}
                  selectedProcessorsID={formData.assignedProcessors}
                  isEditing={isEditing}
                  menuPortal
                />
                {isFieldNotValid('assignedProcessors') && (
                  <p className="xsmallFont text-rush">This is a required Field</p>
                )}
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="midFont">
                <Form.Label>
                  Description <span className="text-muted font-weight-normal">— optional</span>
                </Form.Label>
                <RichTextEditor
                  className="estoppel-notes-rich-text"
                  value={formData.description || ''}
                  onChange={onDescriptionChange}
                  placeholder="Enter description..."
                  enableLists
                />
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group className="midFont mb-2">
                <Form.Label className="text-nowrap">
                  Due In (Business Days)<span className="text-danger">*</span>
                </Form.Label>
                <input
                  id="dueInBusinessDays"
                  onChange={onDueFieldsChange}
                  type="number"
                  min={0}
                  step={1}
                  value={formData.dueInBusinessDays}
                  className={`form-control form-control-sm ${isFieldNotValid('dueInBusinessDays') && 'input-invalid'}`}
                />
                {isFieldNotValid('dueInBusinessDays') && (
                  <p className="xsmallFont text-rush mb-0">This is a required Field</p>
                )}
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group className="midFont mb-2">
                <Form.Label>
                  Due Date<span className="text-danger">*</span>
                </Form.Label>
                <input
                  id="dueDate"
                  onChange={onDueFieldsChange}
                  type="date"
                  value={formData.dueDate}
                  className={`form-control form-control-sm ${isFieldNotValid('dueDate') && 'input-invalid'}`}
                />
                {isFieldNotValid('dueDate') && <p className="xsmallFont text-rush mb-0">This is a required Field</p>}
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Check
                type="checkbox"
                id="excludeFridays"
                className="mb-2"
                label="Exclude Fridays"
                checked={excludeFridays}
                onChange={onExcludeFridaysChange}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row>
            <Col>
              <Button disabled={showProgress} variant="secondary" onClick={onHide} className="mr-2 shadow-none">
                Cancel
              </Button>
              <Button
                disabled={showProgress}
                variant="success"
                onClick={handleSubmit}
                className="shadow-none d-inline-flex align-items-center justify-content-center"
              >
                {showProgress && <Spinner size="sm" animation="border" className="mr-1" />}
                {isEditing ? 'Update Task' : 'Add Task'}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddEditTaskModal;
